import { gql } from 'graphql-request'
import { useForm, Controller } from 'react-hook-form'
import { AlignLeft, Sparkles, Copy, Check, AlertTriangle } from 'lucide-react'
import { useState } from 'react'

import { useMutation } from '@hooks/graphql'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import TextEditor from '@components/TextEditor'
import SecureMarkdown from '@components/SecureMarkdown'
import DisplayAttachment from '@components/DisplayAttachment/DisplayAttachment'
import Collapsible from '@components/Collapsible'

import { useCurrentUser } from '@contexts/currentUser'

import Notification from '@components/Notification'

const UPDATE_FREE_RESPONSE_SUBMISSION = gql`
  mutation UpdateFreeResponseSubmission($input: UpdateFreeResponseSubmissionInput!) {
    updateFreeResponseSubmission(input: $input) {
      blockSubmission {
        id
        status
        blockId
        content {
          ... on FreeResponseSubmission {
            __typename
            answer
            marks
            feedback
            reasoning
            detailedReasoning
            markSchemeSuggestions
            attachments {
              id
              filename
              url
            }
          }
        }
      }
    }
  }
`
const FreeResponseQuestionForm = ({ id, question, markAllocation, answer, marks, feedback, reasoning, detailedReasoning, markSchemeSuggestions, automarkedResult, attachments, refetch, closeModal, autoMarkingError = false }) => {
  const { formState: { errors }, handleSubmit, register, control } = useForm({
    mode: 'onTouched',
    defaultValues: {
      marks,
      feedback
    }
  })

  const [isCopied, setIsCopied] = useState(false)

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
    setIsCopied(true)
    setTimeout(() => setIsCopied(false), 1000)
  }

  const { mutate: updateFreeResponseSubmission, isLoading: isUpdating } = useMutation({
    gqlMutation: UPDATE_FREE_RESPONSE_SUBMISSION,
    onSuccess: () => {
      refetch()
      closeModal()
    }
  })

  const submit = data => updateFreeResponseSubmission({ input: { blockSubmissionId: id, ...data } })

  const { currentMembership: { organization: { features = [] } = {} } = {} } = useCurrentUser()

  const advancedMarkingEnabled = features.includes('advancedMarking')

  return (
    <div>
      <div className='flex items-center mb-5'>
        <AlignLeft className='size-8 mr-3 bg-orange-200 dark:bg-orange-800 rounded-md p-1 shrink-0' />
        <h3 className='text-xl font-bold'>Free Response</h3>
      </div>

      <If condition={autoMarkingError}>
        <Notification theme='error' className='mb-5'>
          <div>
            <div className='flex items-center mb-2'>
              <AlertTriangle className='size-6 text-red-600 dark:text-red-400 mr-2' />
              <h4 className='font-semibold text-red-800 dark:text-red-300'>Automarking Error</h4>
            </div>
            <p className='text-sm text-red-800 dark:text-red-300'>
              Unfortunately there was an automarking error. If the problem persists please contact our support! Lastly note that you may mark this question manually in the meantime.
            </p>
          </div>
        </Notification>

      </If>

      <SecureMarkdown content={question} />

      <div className='bg-gray-100 dark:bg-gray-800 p-3 rounded-lg mt-5'>
        <SecureMarkdown content={answer} />

        <If condition={attachments.length > 0}>
          <div className='flex flex-wrap gap-3'>
            <For each='attachment' of={attachments}>
              <DisplayAttachment filename={attachment.filename} url={attachment.url} outline={false} />
            </For>
          </div>
        </If>
      </div>

      <form onSubmit={handleSubmit(submit)} className='flex flex-col mt-3 gap-3'>
        <div>
          <div className='flex items-end gap-3'>
            <div className='space-y-1'>
              <Label htmlFor='marks'>Marks</Label>
              <Input
                id='marks'
                type='number'
                className='w-[150px]'
                {...register('marks', {
                  valueAsNumber: true,
                  required: 'Marks are required',
                  min: { value: 0, message: "Mark can't be negative" },
                  max: { value: markAllocation, message: 'Mark cannot be greater than the mark allocation' }
                })}
              />
            </div>

            <span className='text-lg font-semibold mb-1'> / {markAllocation}</span>
          </div>

          <If condition={errors.marks}>
            <p className='mt-1 text-sm font-semibold text-red-500' role='alert'>{errors.marks.message}</p>
          </If>
        </div>

        <If condition={automarkedResult && reasoning}>
          <div className='flex bg-purple-100 dark:bg-purple-900 p-3 rounded-lg leading-tight'>
            <Sparkles className='size-5 block mr-2 shrink-0' />
            <SecureMarkdown content={reasoning} />
          </div>
        </If>

        <div className='space-y-1'>
          <Label htmlFor='feedback'>Feedback</Label>
          <TextEditor.Container>
            <Controller
              name='feedback'
              control={control}
              rules={{ required: 'Feedback is required', validate: value => value != null }}
              render={({ field }) => (
                <TextEditor
                  id='feedback'
                  {...field}
                />
              )}
            />

            <TextEditor.Actions>
              <TextEditor.ToolbarToggle />
            </TextEditor.Actions>
          </TextEditor.Container>

          <If condition={errors.feedback}>
            <p className='mt-1 text-sm font-semibold text-red-500' role='alert'>{errors.feedback.message}</p>
          </If>
        </div>

        <If condition={advancedMarkingEnabled}>
          <Collapsible
            label={
              <div className='flex items-center gap-2'>
                Advanced Marking
              </div>
            }
          >
            <div className='flex flex-col gap-4'>
              <If condition={detailedReasoning}>
                <div className='bg-purple-100 dark:bg-purple-900 p-3 rounded-lg relative'>
                  <div className='flex items-center mb-3'>
                    <Sparkles className='size-5 block mr-2 shrink-0' />
                    <h3 className='text-lg font-semibold'>Advanced AI reasoning</h3>
                  </div>

                  <button
                    aria-label='Copy text to clipboard'
                    className='absolute top-3 right-3 rounded-md p-1 cursor-pointer hover:bg-purple-200 dark:hover:bg-purple-800'
                    onClick={() => copyToClipboard(detailedReasoning)}
                    type='button'
                  >
                    <Choose>
                      <When condition={isCopied}>
                        <Check className='h-5 w-5' />
                      </When>
                      <Otherwise>
                        <Copy className='h-5 w-5' />
                      </Otherwise>
                    </Choose>
                  </button>

                  <SecureMarkdown content={detailedReasoning} />
                </div>
              </If>

              <Choose>
                <When condition={Array.isArray(markSchemeSuggestions) && markSchemeSuggestions.length > 0}>
                  <div className='flex flex-col bg-purple-100 dark:bg-purple-900 p-3 rounded-lg leading-tight'>
                    <div className='flex items-center mb-1'>
                      <Sparkles className='size-5 block mr-2 shrink-0' />
                      <h3 className='text-lg font-semibold'>Mark scheme suggestions</h3>
                    </div>
                    <ul className='list-disc pl-5 space-y-1'>
                      <For each='suggestion' index='index' of={markSchemeSuggestions}>
                        <li key={index}>
                          <SecureMarkdown content={suggestion} />
                        </li>
                      </For>
                    </ul>
                  </div>
                </When>
                <When condition={typeof markSchemeSuggestions === 'string' && markSchemeSuggestions.trim() !== ''}>
                  <div className='flex flex-col bg-purple-100 dark:bg-purple-900 p-3 rounded-lg leading-tight'>
                    <div className='flex items-center mb-1'>
                      <Sparkles className='size-5 block mr-2 shrink-0' />
                      <h3 className='text-lg font-semibold'>Mark scheme suggestions</h3>
                    </div>
                    <SecureMarkdown content={markSchemeSuggestions} />
                  </div>
                </When>
              </Choose>
            </div>
          </Collapsible>
        </If>

        <Button
          className='mt-3 w-fit ml-auto'
          type='submit'
          loading={isUpdating}
          disabled={isUpdating}
        >
          Save
        </Button>
      </form>
    </div>
  )
}

export default FreeResponseQuestionForm
